// Shared components for Kova custom sections.

if (!customElements.get('kova-carousel')) {
  customElements.define(
    'kova-carousel',
    class KovaCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.kova-carousel__track');
        if (!this.track) return;
        this.slides = Array.from(this.track.children);
        this.prevButton = this.querySelector('.kova-carousel__arrow--prev');
        this.nextButton = this.querySelector('.kova-carousel__arrow--next');
        this.dotsContainer = this.querySelector('.kova-carousel__dots');
        this.progress = this.querySelector('.kova-carousel__progress');
        this.groupTabs = Array.from(this.querySelectorAll('[data-carousel-group]'));
        this.autoplaySeconds = parseInt(this.dataset.autoplay) || 0;

        this.prevButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
        this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1));
        this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
        // Tabs that jump to the first slide of a group (e.g. size S, M, L).
        this.groupTabs.forEach((tab) =>
          tab.addEventListener('click', () => {
            const index = this.slides.findIndex((slide) => slide.dataset.group === tab.dataset.carouselGroup);
            if (index !== -1) this.goTo(index);
          })
        );

        this.resizeObserver = new ResizeObserver(() => this.setup());
        this.resizeObserver.observe(this.track);

        if (this.autoplaySeconds) {
          ['mouseenter', 'focusin', 'touchstart'].forEach((type) =>
            this.addEventListener(type, () => (this.paused = true), { passive: true })
          );
          ['mouseleave', 'focusout'].forEach((type) => this.addEventListener(type, () => (this.paused = false)));
          this.timer = setInterval(() => {
            if (this.paused || document.hidden) return;
            this.goTo(this.currentIndex + 1 > this.lastIndex ? 0 : this.currentIndex + 1);
          }, this.autoplaySeconds * 1000);
        }
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        clearInterval(this.timer);
      }

      get slideStep() {
        if (this.slides.length < 2) return this.track.clientWidth || 1;
        return this.slides[1].offsetLeft - this.slides[0].offsetLeft || 1;
      }

      setup() {
        const perView = Math.max(1, Math.round(this.track.clientWidth / this.slideStep));
        this.lastIndex = Math.max(0, this.slides.length - perView);
        this.toggleAttribute('data-static', this.lastIndex === 0);

        if (this.dotsContainer && this.dotsContainer.children.length !== this.lastIndex + 1) {
          this.dotsContainer.innerHTML = '';
          for (let i = 0; i <= this.lastIndex; i++) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'kova-carousel__dot';
            dot.setAttribute('aria-label', `${i + 1} / ${this.lastIndex + 1}`);
            dot.addEventListener('click', () => this.goTo(i));
            this.dotsContainer.appendChild(dot);
          }
        }
        this.onScroll();
      }

      onScroll() {
        this.currentIndex = Math.min(this.lastIndex ?? 0, Math.round(this.track.scrollLeft / this.slideStep));
        if (this.prevButton) this.prevButton.disabled = this.currentIndex <= 0;
        if (this.nextButton) this.nextButton.disabled = this.currentIndex >= this.lastIndex;
        this.dotsContainer?.querySelectorAll('.kova-carousel__dot').forEach((dot, index) => {
          dot.setAttribute('aria-current', String(index === this.currentIndex));
        });

        if (this.progress) {
          const { scrollLeft, scrollWidth, clientWidth } = this.track;
          const maxScroll = scrollWidth - clientWidth;
          this.progress.style.setProperty('--progress-size', scrollWidth ? clientWidth / scrollWidth : 1);
          this.progress.style.setProperty('--progress-offset', maxScroll > 0 ? scrollLeft / maxScroll : 0);
        }

        if (this.groupTabs.length) {
          const group = this.slides[this.currentIndex]?.dataset.group;
          this.groupTabs.forEach((tab) => tab.setAttribute('aria-pressed', String(tab.dataset.carouselGroup === group)));
        }
      }

      goTo(index) {
        const target = Math.max(0, Math.min(index, this.lastIndex));
        this.track.scrollTo({ left: target * this.slideStep });
      }
    }
  );
}

if (!customElements.get('kova-in-view')) {
  customElements.define(
    'kova-in-view',
    class KovaInView extends HTMLElement {
      connectedCallback() {
        if (!('IntersectionObserver' in window) || Shopify.designMode) {
          this.classList.add('is-in-view');
          return;
        }
        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (!entry.isIntersecting) return;
            this.classList.add('is-in-view');
            this.observer.disconnect();
            this.countUp();
          },
          { threshold: 0.25 }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      countUp() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this.querySelectorAll('[data-count-to]').forEach((element) => {
          const target = parseFloat(element.dataset.countTo) || 0;
          const duration = 1400;
          const start = performance.now();
          const step = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            element.textContent = Math.round(target * (1 - Math.pow(1 - progress, 3)));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        });
      }
    }
  );
}

if (!customElements.get('before-after-slider')) {
  customElements.define(
    'before-after-slider',
    class BeforeAfterSlider extends HTMLElement {
      connectedCallback() {
        const input = this.querySelector('input[type="range"]');
        if (!input) return;
        const update = () => this.style.setProperty('--position', `${input.value}%`);
        input.addEventListener('input', update);
        update();
      }
    }
  );
}

if (!customElements.get('copy-code')) {
  customElements.define(
    'copy-code',
    class CopyCode extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.label = this.querySelector('[data-label]');
        this.button?.addEventListener('click', this.copy.bind(this));
      }

      async copy() {
        try {
          await navigator.clipboard.writeText(this.dataset.code);
        } catch {
          return;
        }
        const original = this.label.textContent;
        this.label.textContent = this.dataset.copiedLabel || 'Copied!';
        setTimeout(() => (this.label.textContent = original), 2000);
      }
    }
  );
}

if (!customElements.get('rotating-text')) {
  customElements.define(
    'rotating-text',
    class RotatingText extends HTMLElement {
      connectedCallback() {
        this.words = Array.from(this.querySelectorAll('.rotating-text__word'));
        this.container = this.querySelector('.rotating-text__words');
        if (this.words.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this.index = 0;
        this.fitWidth(this.words[0]);
        this.timer = setInterval(() => this.next(), (parseFloat(this.dataset.interval) || 2.5) * 1000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      next() {
        const current = this.words[this.index];
        this.index = (this.index + 1) % this.words.length;
        const upcoming = this.words[this.index];

        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        current.setAttribute('aria-hidden', 'true');
        upcoming.classList.remove('is-leaving');
        upcoming.classList.add('is-active');
        upcoming.removeAttribute('aria-hidden');
        this.fitWidth(upcoming);
        setTimeout(() => current.classList.remove('is-leaving'), 600);
      }

      // Shrink/grow the words box to the active word so centered headings stay balanced.
      fitWidth(word) {
        this.container.style.width = `${word.getBoundingClientRect().width}px`;
      }
    }
  );
}

if (!customElements.get('music-player')) {
  customElements.define(
    'music-player',
    class MusicPlayer extends HTMLElement {
      static STORAGE_KEY = 'kova-music-player';

      connectedCallback() {
        this.audio = this.querySelector('audio');
        this.button = this.querySelector('button');
        this.audio.volume = Math.min(1, Math.max(0, (parseInt(this.dataset.volume) || 50) / 100));
        this.resume = this.dataset.resume === 'true';

        this.button.addEventListener('click', () => (this.audio.paused ? this.play() : this.pause()));
        this.audio.addEventListener('play', () => this.setState(true));
        this.audio.addEventListener('pause', () => this.setState(false));
        window.addEventListener('pagehide', () => this.saveState());

        if (this.resume) this.restore();
      }

      play() {
        this.audio.play().catch(() => this.setState(false));
      }

      pause() {
        this.audio.pause();
      }

      setState(playing) {
        this.classList.toggle('is-playing', playing);
        this.button.setAttribute('aria-pressed', String(playing));
      }

      saveState() {
        if (!this.resume) return;
        try {
          sessionStorage.setItem(
            MusicPlayer.STORAGE_KEY,
            JSON.stringify({ src: this.audio.currentSrc, time: this.audio.currentTime, playing: !this.audio.paused })
          );
        } catch {}
      }

      // Continue where the shopper left off on the previous page. Browsers may block this
      // until the visitor interacts with the page; the button then simply shows "play".
      restore() {
        let state;
        try {
          state = JSON.parse(sessionStorage.getItem(MusicPlayer.STORAGE_KEY));
        } catch {}
        if (!state?.playing) return;
        this.audio.preload = 'auto';
        this.audio.addEventListener('loadedmetadata', () => (this.audio.currentTime = state.time || 0), { once: true });
        this.play();
      }
    }
  );
}

if (!customElements.get('track-order')) {
  customElements.define(
    'track-order',
    class TrackOrder extends HTMLElement {
      static PROVIDERS = {
        '17track': (number) => `https://t.17track.net/en#nums=${encodeURIComponent(number)}`,
        parcelsapp: (number) => `https://parcelsapp.com/en/tracking/${encodeURIComponent(number)}`,
      };

      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.form.querySelector('input');
        this.error = this.querySelector('.track-order__error');
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.track();
        });

        const fromUrl = new URLSearchParams(window.location.search).get('tracking');
        if (fromUrl) {
          this.input.value = fromUrl;
          this.track();
        }
      }

      track() {
        const number = this.input.value.trim().replace(/\s+/g, '');
        this.error.hidden = number.length >= 6;
        if (number.length < 6) return;

        if (this.dataset.mode === 'widget') {
          this.renderWidget(number);
        } else {
          const buildUrl = TrackOrder.PROVIDERS[this.dataset.provider] || TrackOrder.PROVIDERS['17track'];
          window.open(buildUrl(number), '_blank', 'noopener');
        }
      }

      // 17TRACK's public embed script renders the tracking timeline inside our container.
      async renderWidget(number) {
        await TrackOrder.loadWidgetScript();
        window.YQV5.trackSingle({
          YQ_ContainerId: this.dataset.container,
          YQ_Height: 560,
          YQ_Fc: '0',
          YQ_Lang: 'en',
          YQ_Num: number,
        });
      }

      static loadWidgetScript() {
        if (window.YQV5) return Promise.resolve();
        TrackOrder.scriptPromise ??= new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://www.17track.net/externalcall.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
        return TrackOrder.scriptPromise;
      }
    }
  );
}

// "Enlaces de ancla": smooth scroll to the section with that anchor id.
if (!window.kovaJumpLinksBound) {
  window.kovaJumpLinksBound = true;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.jump-links a[href^="#"]');
    if (!link) return;
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', link.hash);
  });
}
